import React, { useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Star, BadgeCheck, ExternalLink, Loader2 } from 'lucide-react';

const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps/search/?api=1&query=Motion+Reality+Unit+9+Croft+Business+Park+Bromborough+CH62+3RA';
// Direct Google Business Profile "write a review" link (g.page/r/.../review).
const GOOGLE_REVIEW_WRITE_URL = 'https://g.page/r/CSnwEYnMDB_5ECE/review';
const TRUSTPILOT_URL = 'https://uk.trustpilot.com/review/motionreality.co.uk';
const TRUSTPILOT_BUSINESS_UNIT_ID = '6928131014306be10e1542a7';

export default function Testimonials() {
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['approved-reviews'],
    queryFn: () => base44.entities.Review.filter({ status: 'approved' }, '-created_date'),
  });

  useEffect(() => {
    if (TRUSTPILOT_BUSINESS_UNIT_ID && window.Trustpilot?.load) {
      window.Trustpilot.load();
    }
  }, []);

  return (
    <section className="py-24 px-6 lg:px-10 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="mb-14">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">TESTIMONIALS</p>
          <h2 className="text-white font-black text-3xl md:text-5xl uppercase leading-tight">
            WHAT DRIVERS SAY
          </h2>
        </div>

        {/* Trustpilot summary */}
        <div className="mb-10 bg-[#111115] border border-white/5 px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="flex gap-1">
              {[...Array(5)].map((_, j) => (
                <Star key={j} className="w-5 h-5 text-primary fill-primary" />
              ))}
            </div>
            <div>
              <p className="text-white font-bold text-sm">RATED BY RACERS ON TRUSTPILOT</p>
              <p className="text-white/40 text-xs mt-0.5">See what customers say across the web</p>
            </div>
          </div>
          {TRUSTPILOT_BUSINESS_UNIT_ID ? (
            <div
              className="trustpilot-widget"
              data-locale="en-GB"
              data-template-id="53aa8807dec7e10d38f59f32"
              data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
              data-style-height="52px"
              data-style-width="100%"
            >
              <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">Trustpilot</a>
            </div>
          ) : null}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 border border-white/5 bg-[#111115]">
            <p className="text-white/30 text-sm">Reviews from our customers will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {reviews.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#111115] border border-white/5 p-7 flex flex-col"
              >
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className={`w-4 h-4 ${j < (t.rating || 5) ? 'text-primary fill-primary' : 'text-white/10'}`} />
                  ))}
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">"{t.quote}"</p>
                <div>
                  <p className="text-white font-bold text-sm">{t.author_name}</p>
                  {t.author_location && <p className="text-white/30 text-xs">{t.author_location}</p>}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                  {t.verified ? (
                    <span className="inline-flex items-center gap-1.5 text-primary/80 text-[10px] font-bold tracking-widest uppercase">
                      <BadgeCheck className="w-3.5 h-3.5" /> Verified Customer
                    </span>
                  ) : (
                    <span className="text-white/30 text-[10px] font-bold tracking-widest uppercase">{t.source_label || t.source}</span>
                  )}
                  {t.source_url ? (
                    <a
                      href={t.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary text-[10px] font-bold tracking-widest uppercase hover:text-primary/80"
                    >
                      VIEW ORIGINAL <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-white/20 text-[10px] font-bold tracking-widest uppercase">On Motion Reality</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-7 py-3.5 transition-colors"
          >
            READ GOOGLE REVIEWS <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href={TRUSTPILOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-7 py-3.5 transition-colors"
          >
            CHECK US OUT ON TRUSTPILOT <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href={GOOGLE_REVIEW_WRITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-7 py-3.5 transition-colors"
          >
            LEAVE A GOOGLE REVIEW <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}